
"use client"

import { useState } from "react"
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, getDate } from "date-fns"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

const CustomCalendar = ({ 
  currentMonth, 
  selectedDate, 
  onDateClick, 
  getHolidayForDate,
  weekendConfig 
}) => {
  const [hoveredDate, setHoveredDate] = useState(null)

  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(currentMonth)
  const startDate = startOfWeek(monthStart)
  const endDate = endOfWeek(monthEnd)

  const dateFormat = "d"
  const rows = []
  let days = []
  let day = startDate

  // Generate calendar grid
  while (day <= endDate) {
    for (let i = 0; i < 7; i++) {
      const formattedDate = format(day, dateFormat)
      const cloneDay = day
      const holiday = getHolidayForDate(day)
      const isCurrentMonth = isSameMonth(day, monthStart)
      const isSelected = selectedDate && isSameDay(day, selectedDate)
      const isToday = isSameDay(day, new Date())

      days.push(
        <div
          key={day}
          className={`
            relative min-h-[80px] p-2 border border-gray-200 cursor-pointer transition-all
            ${!isCurrentMonth ? 'bg-gray-50 text-gray-400' : 'bg-white hover:bg-blue-50'}
            ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''}
            ${isToday ? 'font-bold' : ''}
          `}
          onClick={() => onDateClick(cloneDay)}
          onMouseEnter={() => setHoveredDate(cloneDay)}
          onMouseLeave={() => setHoveredDate(null)}
        >
          {/* Date Number */}
          <div className="flex justify-between items-start mb-1">
            <span className={`
              text-sm font-semibold
              ${isToday ? 'w-6 h-6 flex items-center justify-center rounded-full bg-blue-600 text-white' : ''}
              ${!isCurrentMonth ? 'text-gray-400' : 'text-gray-900'}
            `}>
              {formattedDate}
            </span>
            
            {/* Holiday Indicator Badge */}
            {holiday && isCurrentMonth && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className={`
                      w-2 h-2 rounded-full
                      ${holiday.isWeekend ? 'bg-red-500' : 
                        holiday.type === 'school' ? 'bg-blue-500' :
                        holiday.type === 'public' ? 'bg-green-500' :
                        'bg-purple-500'}
                    `} />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    <p className="font-medium">{holiday.name}</p>
                    <p className="text-xs opacity-80">{holiday.type}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          {/* Holiday Name (if exists and hovering) */}
          {holiday && isCurrentMonth && hoveredDate && isSameDay(hoveredDate, cloneDay) && (
            <div className={`
              text-[10px] font-medium truncate mt-1 px-1 py-0.5 rounded
              ${holiday.isWeekend ? 'bg-red-100 text-red-700' :
                holiday.type === 'school' ? 'bg-blue-100 text-blue-700' :
                holiday.type === 'public' ? 'bg-green-100 text-green-700' :
                'bg-purple-100 text-purple-700'}
            `}>
              {holiday.name}
            </div>
          )}

          {/* Colored Bottom Border for Holidays */}
          {holiday && isCurrentMonth && (
            <div className={`
              absolute bottom-0 left-0 right-0 h-1
              ${holiday.isWeekend ? 'bg-red-500' :
                holiday.type === 'school' ? 'bg-blue-500' :
                holiday.type === 'public' ? 'bg-green-500' :
                'bg-purple-500'}
            `} />
          )}
        </div>
      )
      day = addDays(day, 1)
    }
    rows.push(
      <div key={day} className="grid grid-cols-7">
        {days}
      </div>
    )
    days = []
  }

  // Weekday headers
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

  return (
    <div className="w-full">
      {/* Weekday Headers */}
      <div className="grid grid-cols-7 mb-2">
        {weekDays.map((day, index) => (
          <div 
            key={day} 
            className={`
              text-center font-semibold text-sm py-2 
              ${index === 0 || index === 6 ? 'text-red-600' : 'text-gray-700'}
            `}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="space-y-0 border-t border-l border-gray-200">
        {rows}
      </div>
    </div>
  )
}

export default CustomCalendar
